import { useCollabItemContext } from 'components/CollabItem/CollabItem'
import { CommentForm } from 'components/CommentForm/CommentForm'
import { IconButton } from 'components/IconButton/IconButton'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useRootStore } from 'stores/RootStore'
import Styles from './NewReplyForm.module.scss'
import { ReactComponent as PostIcon } from 'assets/icons/post16.svg'
import { ReactComponent as CancelIcon } from 'assets/icons/close16.svg'
import { NewReplyFormProps } from './Types'
import { useKeyboard } from '@react-aria/interactions'
import { useLocalDebouncedFn } from 'hooks/useLocalDebouncedFn'
import { Toolbar } from 'common/ProseInput/components/Toolbar'

const NewReplyForm = observer(({ item, inputRef, autoFocus }: NewReplyFormProps) => {
  const rootStore = useRootStore()
  const { isReplying, setIsReplying } = useCollabItemContext()
  const [comment, setComment] = useState('')
  const [mentionedUserIds, setMentionedUserIds] = useState<number[]>([])

  const reset = () => {
    setComment('')
    inputRef?.current?.clear?.()
    setMentionedUserIds([])
    setIsReplying(false)
  }

  const handleSubmit = async () => {
    rootStore.comments.create(
      { comment, commentable_id: item.id, commentable_type: 'Comment' },
      mentionedUserIds,
      true,
    )
    reset()
  }

  const handleChange = (newComment: string, mentionedUserIds: number[]) => {
    setIsReplying(newComment !== '')
    setComment(newComment)
    setMentionedUserIds(mentionedUserIds)
  }

  /* -------------------------------------------------------------------------- */
  /*                                  Keyboard                                  */
  /* -------------------------------------------------------------------------- */
  const { keyboardProps } = useKeyboard({
    onKeyDown: (e) => {
      if (e.key === 'Escape') {
        reset()
      }
    },
  })

  /* -------------------------------------------------------------------------- */
  /*                                 Autoscroll                                 */
  /* -------------------------------------------------------------------------- */
  const ref = useRef<HTMLDivElement | null>(null)
  const onType = useLocalDebouncedFn(
    () => {
      ref.current?.scrollIntoView?.({ block: 'nearest' })
    },
    200,
    {},
    [],
  )
  useEffect(() => {
    if (isReplying) {
      onType()
    }
    return () => onType.cancel()
  }, [comment, isReplying, onType])

  return (
    <div {...keyboardProps} ref={ref}>
      <CommentForm
        autoFocus={autoFocus}
        inputRef={inputRef}
        value={comment}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={reset}
        className={Styles.form}
        inputContainerClassName={Styles.inputContainer}
        inputClassName={Styles.input}
        primaryCta={<IconButton variant="primary" title="Post" icon={PostIcon} />}
        secondaryCta={<IconButton variant="tertiary" title="Cancel" icon={CancelIcon} />}
        hideActionsOnEmpty
        isHtmlComment
        footer={
          <Toolbar.Root>
            <Toolbar.Bold />
            <Toolbar.BulletList />
            <Toolbar.Mention />
          </Toolbar.Root>
        }
      />
    </div>
  )
})

export { NewReplyForm }
